/* tslint:disable:max-classes-per-file */

export interface TokenResponse {
    access_token?: string
    refresh_token?: string
    user_id?: string
}

export class CredentialsMercadoLivre {
    sellerId: string
    code: string
    accessToken: string
    refreshToken: string

    constructor(credentials?: Partial<CredentialsMercadoLivre>) {
        this.sellerId = credentials?.sellerId ?? ''
        this.code = credentials?.code ?? ''
        this.accessToken = credentials?.accessToken ?? ''
        this.refreshToken = credentials?.refreshToken ?? ''
    }

    reset(): void {
        this.code = ''
        this.sellerId = ''
        this.accessToken = ''
        this.refreshToken = ''
    }

    setSellerId(sellerInfo: { id: string }): void {
        this.sellerId = sellerInfo.id
    }

    setCode(code: string): void {
        this.code = code
    }

    hasCode(): boolean {
        return !!this.code
    }

    updateTokens(tokens: TokenResponse): void {
        this.accessToken = tokens.access_token ?? ''
        this.refreshToken = tokens.refresh_token ?? ''
    }

    isComplete(): boolean {
        return (
            !!this.code &&
            !!this.sellerId &&
            !!this.accessToken &&
            !!this.refreshToken
        )
    }
}

export class CredentialsMagazineLuiza {
    code: string
    state: string
    access_token: string
    refresh_token: string

    constructor(credentials?: Partial<CredentialsMagazineLuiza>) {
        this.code = credentials?.code ?? ''
        this.state = credentials?.state ?? ''
        this.access_token = credentials?.access_token ?? ''
        this.refresh_token = credentials?.refresh_token ?? ''
    }

    reset(): void {
        this.code = ''
        this.state = ''
        this.access_token = ''
        this.refresh_token = ''
    }

    setCode(code: string): void {
        this.code = code
    }

    setState(state: string): void {
        this.state = state
    }

    hasCode(): boolean {
        return !!this.code
    }

    updateTokens(tokens: TokenResponse): void {
        this.access_token = tokens.access_token ?? ''
        this.refresh_token = tokens.refresh_token ?? ''
    }

    isComplete(): boolean {
        return !!this.access_token && !!this.refresh_token
    }
}

export class CredentialsNuvemShop {
    code: string
    storeId: string
    accessToken: string

    constructor(credentials?: {
        code?: string
        storeId?: string
        userId?: string
        accessToken?: string
    }) {
        this.code = credentials?.code ?? ''
        this.storeId = credentials?.storeId ?? credentials?.userId ?? ''
        this.accessToken = credentials?.accessToken ?? ''
    }

    reset(): void {
        this.code = ''
        this.storeId = ''
        this.accessToken = ''
    }

    setCode(code: string): void {
        this.code = code
    }

    hasCode(): boolean {
        return !!this.code
    }

    updateTokens(tokens: TokenResponse): void {
        this.accessToken = tokens.access_token ?? ''
        this.storeId = tokens.user_id ?? ''
    }

    isComplete(): boolean {
        return !!this.code && !!this.storeId && !!this.accessToken
    }
}

export class CredentialsShopee {
    code: string
    accessToken: string
    refreshToken: string
    shopId: string
    idChannel: string
    subscriberPublicKey: string

    constructor(credentials?: Partial<CredentialsShopee>) {
        this.code = credentials?.code ?? ''
        this.accessToken = credentials?.accessToken ?? ''
        this.refreshToken = credentials?.refreshToken ?? ''
        this.shopId = credentials?.shopId ?? ''
        this.idChannel = credentials?.idChannel ?? ''
        this.subscriberPublicKey = credentials?.subscriberPublicKey ?? ''
    }

    reset(): void {
        this.code = ''
        this.shopId = ''
        this.idChannel = ''
        this.subscriberPublicKey = ''
    }

    setCode(code: string): void {
        this.code = code
    }

    setShopId(shopId: string): void {
        this.shopId = shopId
    }

    hasCode(): boolean {
        return !!this.code
    }

    updateTokens(tokens: TokenResponse): void {
        this.accessToken = tokens.access_token ?? ''
        this.refreshToken = tokens.refresh_token ?? ''
    }

    isComplete(): boolean {
        return (
            !!this.code &&
            !!this.accessToken &&
            !!this.refreshToken &&
            !!this.shopId
        )
    }
}

export class CredentialsWbuy {
    apiPassword: string
    apiUser: string

    constructor(credentials?: Partial<CredentialsWbuy>) {
        this.apiPassword = credentials?.apiPassword ?? ''
        this.apiUser = credentials?.apiUser ?? ''
    }

    reset(): void {
        this.apiPassword = ''
        this.apiUser = ''
    }

    isComplete(): boolean {
        return (
            this.apiPassword !== undefined &&
            this.apiUser !== undefined &&
            this.apiPassword !== '' &&
            this.apiUser !== ''
        )
    }
}

export class CredentialsShopify {
    shop: string
    code: string
    access_token: string

    constructor(credentials?: Partial<CredentialsShopify>) {
        this.shop = credentials?.shop ?? ''
        this.code = credentials?.code ?? ''
        this.access_token = credentials?.access_token ?? ''
    }

    reset(): void {
        this.shop = ''
        this.code = ''
        this.access_token = ''
    }

    setCode(code: string): void {
        this.code = code
    }

    setShop(shop: string): void {
        this.shop = shop
    }

    updateTokens(tokens: any): void {
        if (tokens?.access_token) {
            this.access_token = tokens.access_token
        } 
    }

    isComplete(): boolean {
        return !!this.access_token || !!this.code || !!this.shop
    }
}

export class CredentialsShein {
    openKeyId: string
    secretKey: string
    tempToken: string
    authorizationCompleted: boolean = false

    constructor(credentials?: Partial<CredentialsShein>) {
        this.openKeyId = credentials?.openKeyId ?? ''
        this.secretKey = credentials?.secretKey ?? ''
        this.tempToken = credentials?.tempToken ?? ''
    }

    setCode(tempToken: string): void {
        this.tempToken = tempToken
    }

    reset(): void {
        this.tempToken = '';
        this.openKeyId = '';
        this.secretKey = '';
        this.authorizationCompleted = false;
    }

    setAuthorizationCompleted(completed: boolean): void {
        this.authorizationCompleted = completed
    }

    setCredentials(openKeyId: string, secretKey: string): void {
        this.openKeyId = openKeyId 
        this.secretKey = secretKey
    }

    isComplete(): boolean {
        const ret =
            (this.openKeyId !== undefined &&
            this.openKeyId !== '' &&
            this.secretKey !== undefined &&
            this.secretKey !== '') || 
            (this.authorizationCompleted)
        return ret
    }
}

export class CredentialsTiktokShop {
    code: string
    state: string
    shopRegion: string
    shopId: string
    shopCipher: string
    shopName: string

    constructor(credentials?: Partial<CredentialsTiktokShop>) {
        this.code = credentials?.code ?? ''
        this.state = credentials?.state ?? ''
        this.shopRegion = credentials?.shopRegion ?? ''
        this.shopId = credentials?.shopId ?? ''
        this.shopCipher = credentials?.shopCipher ?? ''
        this.shopName = credentials?.shopName ?? ''
    }

    setCode(code: string): void {
        this.code = code
    }

    setState(state: string): void {
        this.state = state
    }

    setShopRegion(shopRegion: string): void {
        this.shopRegion = shopRegion
    }

    setFromResponse(response?: Partial<CredentialsTiktokShop>): void {
        this.shopId = response?.shopId ?? ''
        this.shopCipher = response?.shopCipher ?? ''
        this.shopName = response?.shopName ?? ''
        this.shopRegion = response?.shopRegion ?? this.shopRegion
        this.code = ''
    }

    reset(): void {
        this.code = ''
        this.state = ''
        this.shopRegion = ''
        this.shopId = ''
        this.shopCipher = ''
        this.shopName = ''
    }

    isComplete(): boolean {
        return !!this.shopId || !!this.shopCipher
    }
}
